<script setup lang="ts">
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import { useRoute } from 'vue-router'
import { computed } from 'vue'
import type { BreadCrumbLink } from '@/types/app'
import { useEntranceBookingsPageQuery } from './EntranceBookingsPage.generated'
import { toTimeString } from '@/libs/utilities/gql-scalar'
import BookingList from '@/components/organisms/BookingList/BookingList.vue'
import { useAlert } from '@/libs/alertPortal'

const route = useRoute('EntranceBookingsPage')
const breadcrumbs = computed<ReadonlyArray<BreadCrumbLink>>(() => {
  const breadcrumbs = route.meta.breadcrumbs ?? []
  return [
    ...breadcrumbs,
    {
      href: '',
      text: `これからの予約一覧`,
    },
  ]
})
const alert = useAlert()

const { fetchMore, result, loading } = useEntranceBookingsPageQuery({
  startAt: toTimeString(new Date()),
  first: 20,
})
const bookingConnection = computed(() => {
  return result.value?.currentEntrance.allBookings
})
const onClickLoadMore = async (after: string | null) => {
  if (bookingConnection.value?.pageInfo.hasNextPage) {
    try {
      await fetchMore({
        variables: {
          after,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult?.currentEntrance.allBookings.edges
          const pageInfo = fetchMoreResult?.currentEntrance.allBookings.pageInfo

          return newEdges?.length && pageInfo
            ? {
                ...previousResult,
                currentEntrance: {
                  ...previousResult.currentEntrance,
                  allBookings: {
                    ...previousResult.currentEntrance.allBookings,
                    edges: [
                      ...previousResult.currentEntrance.allBookings.edges,
                      ...newEdges,
                    ],
                    pageInfo,
                  },
                },
              }
            : previousResult
        },
      })
    } catch (e) {
      console.error(e)
      alert.error('エラーが発生しました。再度お試しください。')
    }
  }
}
</script>
<template>
  <CenteredPageContainer :class="$style.page">
    <BreadcrumbList :links="breadcrumbs" />
    <main :class="[$style.page__main, $style.main]">
      <SectionTitle>これからの予約一覧</SectionTitle>
      <div :class="$style.main__content">
        <BookingList
          v-if="bookingConnection && bookingConnection.edges.length > 0"
          :booking-connection="bookingConnection"
          :is-loading="loading"
          @click-load-more="onClickLoadMore"
        />
        <div v-else-if="!loading" :class="$style.main__notFound">
          現在予約はありません
        </div>
      </div>
    </main>
  </CenteredPageContainer>
</template>

<style lang="scss" module>
.page {
  &__main {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.main {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__content {
    background-color: $color-surface-primary;
    border-radius: $border-radius-v2-rounder;
    box-shadow: $shadow-24;
    padding: 24px;
  }

  &__notFound {
    padding: 24px;
    text-align: center;
    color: $color-text-and-object-low-emphasis;
  }
}
</style>
